/**
 * Метки и группы типов запросов (как во вкладках поиска / истории).
 */

export const REQUEST_GROUPS = [
  {
    key: 'name',
    label: 'Поиск по названию',
    types: ['name', 'requests', 'wipoName'],
  },
  {
    key: 'address',
    label: 'Поиск по адресу',
    types: ['adress', 'adressReq', 'wipoAdres'],
  },
  {
    key: 'holder',
    label: 'Поиск по правообладателю',
    types: ['patentObl', 'patentOblRequests', 'wipoObl'],
  },
  {
    key: 'konstruktor',
    label: 'Конструктор поиска',
    types: ['konstruktor', 'konstruktor_req', 'konstruktor_wipo'],
  },
  {
    key: 'image',
    label: 'Поиск по изображению',
    types: ['image', 'imageReq', 'imageWipo', 'yandexImageSearch'],
  },
  {
    key: 'description',
    label: 'Поиск по описанию',
    types: [
      'description',
      'description_tz',
      'description_req',
      'description_wipo',
      'description_elastic',
    ],
  },
  {
    key: 'similar',
    label: 'Похожие / аналоги',
    types: ['similar_brand', 'similar_analog'],
  },
  {
    key: 'ai',
    label: 'AI-аналитика',
    types: ['getAiAnalitics'],
  },
];

const TYPE_TO_GROUP = {};
for (const g of REQUEST_GROUPS) {
  for (const t of g.types) {
    TYPE_TO_GROUP[t] = g.key;
  }
}

export function requestTypeLabel(type) {
  switch (type) {
    case 'image':
      return 'ТЗ';
    case 'imageReq':
      return 'Заявки';
    case 'imageWipo':
      return 'WIPO';
    case 'name':
      return 'ТЗ';
    case 'requests':
      return 'Заявки';
    case 'wipoName':
      return 'WIPO';
    case 'adress':
      return 'ТЗ';
    case 'adressReq':
      return 'Заявки';
    case 'wipoAdres':
      return 'WIPO';
    case 'patentObl':
      return 'ТЗ';
    case 'patentOblRequests':
      return 'Заявки';
    case 'wipoObl':
      return 'WIPO';
    case 'konstruktor':
      return 'ТЗ';
    case 'konstruktor_req':
      return 'Заявки';
    case 'konstruktor_wipo':
      return 'WIPO';
    case 'description_tz':
      return 'ТЗ';
    case 'description_req':
      return 'Заявки';
    case 'description_wipo':
      return 'WIPO';
    case 'description':
      return 'Содержание (тип 1)';
    case 'description_elastic':
      return 'Содержание (тип 2)';
    case 'similar_brand':
      return 'Похожие бренды';
    case 'similar_analog':
      return 'Аналоги';
    case 'yandexImageSearch':
      return 'Глобальный поиск';
    case 'getAiAnalitics':
      return 'AI-аналитика';
    default:
      return type || 'Запрос';
  }
}

export function getRequestGroupKey(type) {
  return TYPE_TO_GROUP[type] || type;
}

export function getRequestGroupLabel(groupKey) {
  const group = REQUEST_GROUPS.find((g) => g.key === groupKey);
  if (group) return group.label;
  return requestTypeLabel(groupKey);
}

export function formatGroupWithSources(groupKey) {
  const group = REQUEST_GROUPS.find((g) => g.key === groupKey);
  if (!group) return requestTypeLabel(groupKey);
  const sources = [...new Set(group.types.map(requestTypeLabel))].join(', ');
  return `${group.label} (${sources})`;
}

export function aggregateCountsByGroup(items, typeField = 'type', countField = 'count') {
  const map = {};
  for (const item of items || []) {
    const rawType = item[typeField];
    const key = getRequestGroupKey(rawType);
    const add = countField != null ? Number(item[countField]) : 1;
    map[key] = (map[key] || 0) + add;
  }
  return map;
}

export function aggregateByTypeObject(byType) {
  return aggregateCountsByGroup(
    Object.entries(byType || {}).map(([type, count]) => ({ type, count }))
  );
}

export function getOrderedGroupKeys(countsByGroup) {
  const keys = new Set(Object.keys(countsByGroup || {}));
  const ordered = REQUEST_GROUPS.map((g) => g.key).filter((k) => keys.has(k));
  for (const k of keys) {
    if (!ordered.includes(k)) ordered.push(k);
  }
  return ordered;
}

export function groupKeysFromMeta(requestTypes) {
  const keys = new Set((requestTypes || []).map(getRequestGroupKey));
  return getOrderedGroupKeys(Object.fromEntries([...keys].map((k) => [k, 1])));
}

/** Ключи полей description, по которым определяется тип запроса */
export const DESCRIPTION_TYPE_KEYS = (() => {
  const keys = new Set();
  for (const g of REQUEST_GROUPS) {
    for (const t of g.types) keys.add(t);
  }
  keys.add('check_search');
  keys.add('naming_search');
  return [...keys];
})();

export function detectRequestTypeFromDescription(description) {
  if (!description || typeof description !== 'object') return null;
  return Object.keys(description).find((key) => DESCRIPTION_TYPE_KEYS.includes(key)) || null;
}

/** Развёрнутая подпись типа для истории запросов (с названием вкладки) */
export function formatRequestTypeDetailed(type) {
  switch (type) {
    case 'image':
      return 'Поиск по изображению (ТЗ)';
    case 'imageReq':
      return 'Поиск по изображению (заявки)';
    case 'imageWipo':
      return 'Поиск по изображению (WIPO)';
    case 'name':
      return 'Поиск по названию (ТЗ)';
    case 'requests':
      return 'Поиск по названию (заявки)';
    case 'adress':
      return 'Поиск по адресу (ТЗ)';
    case 'adressReq':
      return 'Поиск по адресу (заявки)';
    case 'patentObl':
      return 'Поиск по правообладателю (ТЗ)';
    case 'patentOblRequests':
      return 'Поиск по правообладателю (заявки)';
    case 'description':
      return 'Поиск по содержанию (тип 1)';
    case 'description_tz':
      return 'Поиск по описанию (ТЗ)';
    case 'description_req':
      return 'Поиск по описанию (заявки)';
    case 'description_wipo':
      return 'Поиск по описанию (ВИПО)';
    case 'description_elastic':
      return 'Поиск по содержанию (тип 2)';
    case 'konstruktor':
      return 'Поиск по конструктору (ТЗ)';
    case 'konstruktor_req':
      return 'Поиск по конструктору (заявки)';
    case 'konstruktor_wipo':
      return 'Поиск по конструктору (ВИПО)';
    case 'similar_brand':
      return 'Поиск похожих брендов';
    case 'similar_analog':
      return 'Поиск аналогов';
    case 'check_search':
      return 'Проверка по 1483';
    case 'naming_search':
      return 'Поиск по наименованию';
    case 'wipoName':
      return 'WIPO: по названию';
    case 'wipoAdres':
      return 'WIPO: по адресу';
    case 'wipoObl':
      return 'WIPO: по правообладателю';
    case 'yandexImageSearch':
      return 'Глобальный поиск';
    case 'getAiAnalitics':
      return 'AI-аналитика';
    default:
      return type || 'Запрос';
  }
}

export function getGroupLabelFromTasks(tasks) {
  if (!tasks?.length) return 'Поиск';
  const types = tasks
    .map((t) => detectRequestTypeFromDescription(t.description))
    .filter(Boolean);
  if (types.some((t) => t === 'yandexImageSearch')) return 'Глобальный поиск';
  const first = types[0];
  if (!first) return 'Поиск';
  return getRequestGroupLabel(getRequestGroupKey(first));
}
